import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { Api } from '../api/api';
import type { AccountProfile, Child } from '../api/models';
import { setChildModeLostHandler } from '../api/client';
import { TokenStore, isChildActive } from '../api/tokenStore';
import { useFocusOnScreenChange } from './useFocusOnScreenChange';
import { AppText } from '../components/AppText';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { VerifyEmailPage } from '../pages/VerifyEmailPage';
import { SymbolSheetPage } from '../pages/SymbolSheetPage';
import { ForgotPasswordScreen } from '../screens/ForgotPassword/ForgotPasswordScreen';
import { RegisterScreen } from '../screens/Register/RegisterScreen';
import { SignInScreen } from '../screens/SignIn/SignInScreen';
import { WelcomeScreen } from '../screens/Welcome/WelcomeScreen';
import { Strings } from '../strings';

import { ChildShell } from './ChildShell';
import { OauthLanding } from './OauthLanding';
import { ParentShell } from './ParentShell';
import { ChildRoute, ParentRoute, PublicRoute } from './routes';

/**
 * Which face of the client is showing. The two authenticated shells are **exclusive, not nested**
 * (ADR-0006): no route leads from a child screen to a parent screen, and entering child mode replaces the
 * shell.
 * Mirrors android/…/Navigation/AppShell.kt.
 *
 * On the web the shells are also the URL space, and the guard below is what makes that safe: while a child
 * token is active every `/parent/*` URL redirects to child home, and while one is not every `/child/*` URL
 * redirects to the parent's children list. Typing the other address does not cross the boundary, and the
 * API refuses the crossing regardless — the address bar is convenience, never authority.
 */
type Shell =
  | { kind: 'loading' }
  | { kind: 'anonymous' }
  | { kind: 'parent'; email: string; emailVerified: boolean }
  | { kind: 'child'; childId: string; nickname: string };

export function AppShell() {
  const [shell, setShell] = useState<Shell>({ kind: 'loading' });
  const location = useLocation();
  // Entering a screen moves focus to its heading, so the screen announces itself (R0121).
  useFocusOnScreenChange();
  const [notice, setNotice] = useState<string | null>(null);
  // Carried from registration to sign-in. Signing them in automatically is not an option: the API answers a
  // registration the same way whether or not the address was taken, so a session here would go to whoever
  // typed someone else's address (R0031). Carrying the address across is the whole of the fix (R0080).
  const [registeredEmail, setRegisteredEmail] = useState('');
  // Kept across the switch into child mode so that leaving it can restore the parent shell **without an
  // await**. Going through `/auth/me` again would leave the child screens mounted for a tick after their
  // token was cleared, and their next fetch would be a child-scoped call carrying a parent token.
  const [parentEmail, setParentEmail] = useState<string>('');
  // Kept beside the address so leaving child mode restores the banner state rather than losing it.
  const [parentEmailVerified, setParentEmailVerified] = useState<boolean>(true);
  const navigate = useNavigate();

  // A reload in child mode must land back in child mode: the token outlives the page, so the shell has to
  // be derived from the token rather than from what the last click happened to be.
  const restore = useCallback(async (): Promise<void> => {
    // Child mode is decided **before** any request, and deliberately so. `/auth/me` is a parent endpoint,
    // and `activeAccessToken` prefers the child token — so asking who is signed in while a child profile
    // is active sends a child-scoped token to a parent endpoint, gets the 403 ADR-0006 promises, and
    // signs the family out on a page reload. The child shell needs nothing from that call anyway.
    if (isChildActive()) {
      const childId = TokenStore.readActiveChildId() ?? '';

      // The nickname is not in the token, and a child token cannot read `/children`. Rather than show a
      // greeting to nobody, the shell keeps the name it was given when the profile was chosen and falls
      // back to an empty one, which the greeting template already handles.
      setShell({ kind: 'child', childId, nickname: readRememberedNickname() });

      return;
    }

    if (TokenStore.readParentAccessToken() === null) {
      setShell({ kind: 'anonymous' });

      return;
    }

    const me = await Api.readMe();

    if (!me.ok) {
      TokenStore.clearAll();
      setShell({ kind: 'anonymous' });

      return;
    }

    setParentEmail(me.value.email);
    setParentEmailVerified(me.value.emailVerified !== false);
    setShell({ kind: 'parent', email: me.value.email, emailVerified: me.value.emailVerified !== false });
  }, []);

  useEffect(() => {
    void restore();
  }, [restore]);

  /**
   * Watch for the address being confirmed somewhere else (R0098).
   *
   * The mail link opens `/verify-email` in a **different tab**, so nothing tells the running app that the
   * fact it read at start-up has changed — the banner stayed until the parent reloaded by hand.
   *
   * It runs only while a parent is signed in and unverified, and the interval is cleared the moment it
   * sees otherwise, so a verified account polls nothing. `visibilitychange` covers the common case
   * directly: the parent confirms in the other tab and comes back, and the banner is gone before the next
   * tick would have fired.
   */
  // Extracted so the dependency is a plain value the linter can check, rather than an expression.
  const isAwaitingVerification = shell.kind === 'parent' && !shell.emailVerified;

  useEffect(() => {
    if (!isAwaitingVerification) {
      return;
    }

    let cancelled = false;

    const recheck = async (): Promise<void> => {
      const me = await Api.readMe();

      if (cancelled || !me.ok || me.value.emailVerified === false) {
        return;
      }

      setParentEmailVerified(true);
      setShell((current: Shell): Shell =>
        current.kind === 'parent' ? { ...current, emailVerified: true } : current,
      );
    };

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        void recheck();
      }
    };

    const timer = window.setInterval(() => void recheck(), VERIFICATION_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [shell.kind, isAwaitingVerification]);

  // If child mode cannot be renewed — the parent's session has gone too — the client clears the token and
  // calls this. The shell decides where the user lands; the network layer only reports that it happened.
  useEffect(() => {
    setChildModeLostHandler(() => {
      setShell({ kind: 'anonymous' });
      navigate(PublicRoute.signIn, { replace: true });
    });

    return () => setChildModeLostHandler(null);
  }, [navigate]);

  const signedIn = (account: AccountProfile): void => {
    setNotice(null);
    setParentEmail(account.email);
    setParentEmailVerified(account.emailVerified !== false);
    setShell({ kind: 'parent', email: account.email, emailVerified: account.emailVerified !== false });
    navigate(ParentRoute.children, { replace: true });
  };

  const enteredChildMode = (child: Child): void => {
    rememberNickname(child.nickname);
    setShell({ kind: 'child', childId: child.id, nickname: child.nickname });
    navigate(ChildRoute.home, { replace: true });
  };

  const leaveChildMode = (): void => {
    // Synchronous, in this order: the shell is replaced in the same tick the token is cleared, so no child
    // screen survives the switch long enough to make a request without one (ADR-0006 — the shells are
    // exclusive states, not two branches that briefly overlap).
    TokenStore.clearChildToken();
    setShell({ kind: 'parent', email: parentEmail, emailVerified: parentEmailVerified });
    navigate(ParentRoute.children, { replace: true });
  };

  const signOut = (): void => {
    void Api.signOut();
    TokenStore.clearAll();
    setShell({ kind: 'anonymous' });
    navigate(PublicRoute.welcome, { replace: true });
  };

  // Verification is reachable in **every** shell state, and is checked before the switch below.
  //
  // A parent is usually signed in on the device they read their mail on, and the parent and child branches
  // both send every unrecognised address home — so the link would have redirected to /parent/children and
  // confirmed nothing, silently, in the commonest case (R0066).
  // The provider landing, like verification, is reachable in **every** shell state: the redirect arrives
  // before this app knows whether anybody is signed in, and every branch below sends an unrecognised
  // address home — which would drop the tokens on the floor (R0148).
  if (location.pathname === PublicRoute.oauthCallback) {
    return (
      <Routes>
        <Route path={PublicRoute.oauthCallback} element={<OauthLanding />} />
      </Routes>
    );
  }

  if (location.pathname === PublicRoute.verifyEmail) {
    return (
      <Routes>
        <Route path={PublicRoute.verifyEmail} element={<VerifyEmailPage />} />
      </Routes>
    );
  }

  switch (shell.kind) {
    case 'loading':
      return (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
          <AppText color="var(--text-secondary)">{Strings.appName}</AppText>
        </div>
      );

    case 'child':
      return (
        <Routes>
          <Route
            path="/child/*"
            element={<ChildShell nickname={shell.nickname} onLeaveChildMode={leaveChildMode} />}
          />
          {/* Every other address, parent screens included, goes home. The boundary is not a hidden link. */}
          <Route path="*" element={<Navigate to={ChildRoute.home} replace />} />
        </Routes>
      );

    case 'parent':
      return (
        <Routes>
          <Route
            path="/parent/*"
            element={
              <ParentShell
                email={shell.email}
                emailVerified={shell.emailVerified}
                onEnteredChildMode={enteredChildMode}
                onSignOut={signOut}
              />
            }
          />
          <Route path="*" element={<Navigate to={ParentRoute.children} replace />} />
        </Routes>
      );

    default:
      return (
        <div
          style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          <div style={{ width: '100%', maxWidth: 'var(--column)', margin: '0 auto' }}>
            <Routes>
              <Route
                path={PublicRoute.welcome}
                element={
                  <WelcomeScreen
                    onCreateAccount={() => navigate(PublicRoute.register)}
                    onSignIn={() => navigate(PublicRoute.signIn)}
                  />
                }
              />
              <Route
                path={PublicRoute.signIn}
                element={
                  <SignInScreen
                    notice={notice}
                    initialEmail={registeredEmail}
                    onSignedIn={signedIn}
                    onForgotPassword={() => navigate(PublicRoute.forgotPassword)}
                    onCreateAccount={() => navigate(PublicRoute.register)}
                    onBackToIntro={() => navigate(PublicRoute.welcome)}
                  />
                }
              />
              <Route
                path={PublicRoute.register}
                element={
                  <RegisterScreen
                    onRegistered={(email: string) => {
                      setNotice(Strings.registerDone);
                      setRegisteredEmail(email);
                      navigate(PublicRoute.signIn);
                    }}
                    onHaveAccount={() => navigate(PublicRoute.signIn)}
                  />
                }
              />
              <Route
                path={PublicRoute.forgotPassword}
                element={<ForgotPasswordScreen onBack={() => navigate(PublicRoute.signIn)} />}
              />
              <Route path={PublicRoute.resetPassword} element={<ResetPasswordPage />} />
              <Route path={PublicRoute.verifyEmail} element={<VerifyEmailPage />} />
              {/* A development tool for reviewing the symbol catalogue (R0045). Never in a production
                  bundle, and never reachable from the product. */}
              {import.meta.env.DEV ? <Route path="/symbols" element={<SymbolSheetPage />} /> : null}
              <Route path="*" element={<Navigate to={PublicRoute.welcome} replace />} />
            </Routes>
          </div>
        </div>
      );
  }
}

const NICKNAME_KEY = 'myslikrok.child.nickname';

const rememberNickname = (nickname: string): void => {
  try {
    window.sessionStorage.setItem(NICKNAME_KEY, nickname);
  } catch {
    // Storage is unavailable; the greeting falls back to an empty name, which the template handles.
  }
};

const readRememberedNickname = (): string => {
  try {
    return window.sessionStorage.getItem(NICKNAME_KEY) ?? '';
  } catch {
    return '';
  }
};

/**
 * How often to ask whether the address has been confirmed.
 *
 * Fifteen seconds: long enough that an unverified parent leaving the app open costs four requests a
 * minute, short enough that a parent who confirms in another tab and switches back sees the banner go
 * without wondering whether it worked. `visibilitychange` usually gets there first.
 */
const VERIFICATION_POLL_MS = 15_000;
